import { gql, useApolloClient } from '@apollo/client';
import { LanguageId } from 'lib-knowledge-management-common/graphql';
import { useCallback } from 'react';

export type CategoryNameLookup = (
  categoryId: string | undefined,
  languageId: LanguageId,
) => string | undefined;

/**
 * Only the titles, so the read succeeds against any category in the cache.
 * `CategoryFields` would also ask for `parent` and `restrictions`, and a
 * fragment read returns nothing at all when one selected field is missing.
 */
const CATEGORY_TITLES = gql`
  fragment SuggestionCategoryTitles on Category {
    id
    titles {
      languageId
      title
    }
  }
`;

type CategoryTitles = {
  titles: { languageId: LanguageId; title: string }[];
};

/**
 * Resolves a category id to its name, in the language asked for.
 *
 * Reads the category out of the cache by id. Anything the cache holds resolves,
 * however it got there — `useSeedCategoryTitles`, a collection's own tree, or
 * the select the reviewer just picked from.
 *
 * A fragment read is not reactive, so a caller deriving names from this has to
 * re-run when the cache gains categories. `useSeedCategoryTitles` returns a
 * counter for exactly that.
 *
 * A title is per-language, and a category can be untranslated for the language
 * the AI wrote in, so the first title stands in when there is no match.
 */
export function useCategoryNames(): CategoryNameLookup {
  const { cache } = useApolloClient();

  return useCallback(
    (categoryId, languageId) => {
      if (!categoryId) return undefined;

      const category = cache.readFragment<CategoryTitles>({
        id: cache.identify({ __typename: 'Category', id: categoryId }),
        fragment: CATEGORY_TITLES,
        fragmentName: 'SuggestionCategoryTitles',
      });

      const titles = category?.titles;
      if (!titles?.length) return undefined;

      return (
        titles.find((title) => title.languageId === languageId)?.title ??
        titles[0]?.title
      );
    },
    [cache],
  );
}
